/**
 * Reply from the phone: a deliberate second screen (scope §5.11), never the default. The send box follows
 * the same rules as the desktop composer: it is withheld (no button) whenever the API says so, the text is
 * checked by the compose guard before Send appears, and the API runs the send-phase guard on the final bytes.
 * Owner: web-phone-pwa.
 */
import { Button, Chip, ErrorBanner, Skeleton, Textarea } from '@patchbay/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Send } from 'lucide-react'
import * as React from 'react'
import { initialChoice, PostSendChips, type PostSendChoice, preselect, toSendFields } from '../composer/post-send-plan'
import { type ActionTarget, LogTimeSheet } from './actions'
import { composeCheck, describeError, type GuardVerdict, type SendOut, sendReply, sendStateQuery, uuid } from './api'

/** Plain phone text to the HTML the API takes: escaped, one <p> per paragraph, line breaks kept. */
export function textToHtml(text: string): string {
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  return text
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${esc(p).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

const UNDO_SECONDS = 5

/** Nothing about a verdict may be read as "allowed" unless every field says so. */
export function canSend(v: GuardVerdict | null): boolean {
  if (!v?.allowed || v.send_box !== 'enabled') return false
  if ((v.withheld_reasons ?? []).length > 0) return false
  if (!(v.to ?? []).length) return false
  return !Object.values(v.scrub_report ?? {}).some((n) => n > 0)
}

export function PhoneReply({
  threadId,
  audienceLabel,
  target,
  sendBlock,
  onBack,
}: {
  threadId: string
  audienceLabel: string
  target: ActionTarget
  /** Offline, mirror stale, or a role that writes nothing to Desk. */
  sendBlock: string | null
  onBack: () => void
}) {
  const state = useQuery(sendStateQuery(threadId))
  const [text, setText] = React.useState('')
  const [verdict, setVerdict] = React.useState<GuardVerdict | null>(null)
  const [countdown, setCountdown] = React.useState<number | null>(null)
  const [sent, setSent] = React.useState<SendOut | null>(null)
  const [timeOpen, setTimeOpen] = React.useState(false)
  const key = React.useRef(uuid())
  // #746: the same post-send chip row as the desk composer, minus Close (the phone has no Close dialog). Only a reply
  // to the customer can wait on them, and a person's own pick always wins over the guard's reading.
  const audience = audienceLabel === 'Customer' ? 'customer' : 'contractor'
  const [choice, setChoice] = React.useState<PostSendChoice>(() => initialChoice())
  const touched = React.useRef(false)
  const bodyHtml = textToHtml(text)

  const check = useMutation({
    networkMode: 'always',
    mutationFn: () => composeCheck({ thread_id: threadId, body_html: bodyHtml }),
    onSuccess: (v) => {
      setVerdict(v)
      if (touched.current) return
      const action = preselect(audience, v.reply_intent, Boolean(v.asks_customer))
      setChoice((current) => ({ ...current, action: action === 'close' ? 'none' : action }))
    },
  })
  const send = useMutation({
    networkMode: 'always',
    mutationFn: () => {
      if (!verdict) throw new Error('Check the reply first.')
      return sendReply({
        thread_id: threadId,
        body_html: bodyHtml,
        to: verdict.to ?? [],
        cc: verdict.cc ?? [],
        idempotency_key: key.current,
        post_send: toSendFields(choice, audience, null).post_send,
      })
    },
    onSuccess: (out) => {
      // #186: a reply asks for nothing; "+ time" is there if the person wants it.
      setSent(out)
    },
  })

  const box = state.data?.data
  const withheld = box && box.send_box !== 'enabled'
  const blocked = sendBlock ?? (withheld ? (box.reason ?? `Send box ${box.send_box.replace('_', ' ')}`) : null)

  // The 5-second "Sending to Desk... Cancel" bar. Going offline or a withheld box cancels it.
  const fire = React.useRef(send.mutate)
  fire.current = send.mutate
  React.useEffect(() => {
    if (countdown === null) return
    if (blocked) {
      setCountdown(null)
      return
    }
    if (countdown <= 0) {
      setCountdown(null)
      fire.current()
      return
    }
    const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000)
    return () => clearTimeout(t)
  }, [countdown, blocked])

  return (
    <div className="flex min-h-full flex-col">
      <div className="flex items-center gap-2 px-2 py-2">
        <Button variant="ghost" className="h-11" onClick={onBack}>
          <ArrowLeft className="size-5" /> Case
        </Button>
        <p className="ml-auto pr-2 text-sm text-muted-foreground">{target.number}</p>
      </div>
      <div className="space-y-3 px-4 pb-4">
        <h1 className="text-2xl font-bold">Reply to {audienceLabel}</h1>
        {sent ? (
          <div className="space-y-3">
            <p className="rounded-lg border border-success/30 bg-success-soft px-3 py-3 text-base text-success" role="status">
              Sent to Desk. Thread {sent.desk_thread_id}.
            </p>
            <Button variant="outline" className="h-12 w-full text-base" onClick={() => setTimeOpen(true)}>
              + time (optional)
            </Button>
            <Button variant="ghost" className="h-12 w-full" onClick={onBack}>
              Back to the case
            </Button>
            <LogTimeSheet
              open={timeOpen}
              onOpenChange={setTimeOpen}
              target={target}
              source="send_prompt"
              promptRef={sent.time_prompt.prompt_ref}
              defaultMinutes={sent.time_prompt.default_minutes}
              defaultSource={sent.time_prompt.default_source}
              defaultBillable={sent.time_prompt.billable_default}
              billableBasis={sent.time_prompt.billable_basis}
              domains={sent.time_prompt.domains}
              defaultDomain={sent.time_prompt.domain}
              writeBlock={null}
            />
          </div>
        ) : state.isError ? (
          <ErrorBanner title="Can't tell whether you can send" detail={`${describeError(state.error)} Sending is off until it answers.`} />
        ) : !box ? (
          <Skeleton className="h-32 w-full" />
        ) : blocked ? (
          // Withheld, not warned: the reason and no button.
          <div
            role="status"
            className="rounded-lg border border-warning/40 bg-warning-soft px-3 py-3 text-base text-warning"
            data-send-box="withheld"
          >
            <p className="font-medium">Send box withheld</p>
            <p className="text-foreground/80">{blocked}</p>
          </div>
        ) : (
          <>
            <Textarea
              aria-label="Reply text"
              value={text}
              readOnly={countdown !== null}
              onChange={(e) => {
                setText(e.target.value)
                setVerdict(null)
              }}
              rows={8}
              className="text-base"
              placeholder="Your reply. Your signature is added when it sends."
            />
            {check.isError ? <ErrorBanner title="Guard check failed; not sent" detail={describeError(check.error)} /> : null}
            {verdict ? <VerdictView v={verdict} /> : null}
            {canSend(verdict) && audience === 'customer' ? (
              <PostSendChips
                choice={choice}
                audience={audience}
                canClose={false}
                onChange={(next) => {
                  touched.current = true
                  setChoice(next)
                }}
              />
            ) : null}
            {send.isError ? <ErrorBanner title="Not sent" detail={describeError(send.error)} /> : null}
            {countdown !== null ? (
              <div className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary-soft px-3 py-3" role="status">
                <span className="flex-1 text-base">Sending to Desk in {countdown}s...</span>
                <Button className="h-11" onClick={() => setCountdown(null)}>
                  Cancel
                </Button>
              </div>
            ) : canSend(verdict) ? (
              <Button
                variant="primary"
                className="h-14 w-full text-base"
                disabled={send.isPending}
                onClick={() => setCountdown(UNDO_SECONDS)}
              >
                <Send className="size-5" /> Send to {verdict?.to?.join(', ')}
              </Button>
            ) : (
              <Button
                variant="primary"
                className="h-14 w-full text-base"
                disabled={!text.trim() || check.isPending}
                onClick={() => check.mutate()}
              >
                Check before sending
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function VerdictView({ v }: { v: GuardVerdict }) {
  const scrubbed = Object.entries(v.scrub_report ?? {}).filter(([, n]) => n > 0)
  return (
    <div className="space-y-2 text-sm">
      <p className="flex flex-wrap items-center gap-1.5">
        <span className="text-muted-foreground">To</span>
        {(v.to ?? []).map((a) => (
          <Chip key={a}>{a}</Chip>
        ))}
        {(v.cc ?? []).length ? <span className="ml-2 text-muted-foreground">Cc</span> : null}
        {(v.cc ?? []).map((a) => (
          <Chip key={a}>{a}</Chip>
        ))}
      </p>
      {(v.removed_recipients ?? []).map((r) => (
        <p key={JSON.stringify(r)} className="text-xs text-muted-foreground">
          Removed {Object.values(r).join(' · ')}
        </p>
      ))}
      {scrubbed.length ? (
        <p className="rounded-md bg-warning-soft px-2 py-1.5 text-warning">
          Remove the flagged text before sending: {scrubbed.map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`).join(', ')}.
        </p>
      ) : null}
      {(v.withheld_reasons ?? []).map((r) => (
        <p key={r.code} className="rounded-md bg-warning-soft px-2 py-1.5 text-warning">
          {r.message}
        </p>
      ))}
      {(v.warnings ?? []).map((r) => (
        <p key={r.code} className="text-xs text-muted-foreground">
          {r.message}
        </p>
      ))}
      {v.send_box !== 'enabled' ? <p className="text-warning">{v.send_box_reason ?? 'Send box withheld.'}</p> : null}
    </div>
  )
}
